import { Autocomplete, AutocompleteProps, TextField } from '@mui/material';
import { getProjectCodeLibraries } from 'api';
import { CodeLibrary } from 'api/models';
import { OptionSourceType } from 'api/models/FieldTemplate';
import { DEFAULT_SELECT_OPTIONS } from 'components/GlobalDialog/AddFieldTemplate/forms/utils';
import { FormikProps } from 'formik';
import useRequest from 'hooks/useRequest';
import { query } from 'operations';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { GeneralIds, ModelTypes } from 'types/types';
import { AddFieldValues } from '../../AddFieldTemplateDialog';

type AutocompletePropsType = Omit<
  AutocompleteProps<CodeLibrary, false, false, false>,
  'options' | 'getOptionLabel' | 'renderInput'
>;

interface CodeLibraryAutocompleteProps extends AutocompletePropsType {
  formik: FormikProps<AddFieldValues>;
  disabled?: boolean;
}

export default function CodeLibraryAutocomplete({
  formik,
  disabled,
}: CodeLibraryAutocompleteProps) {
  const { t, i18n } = useTranslation();
  const { projectId } = useParams<GeneralIds>();

  const { data: libraries, loading: isLoading } = useRequest<CodeLibrary[]>(
    async () => {
      const { data: libraries } = await query<CodeLibrary[]>({
        type: ModelTypes.codeLibraries,
        ids: {
          projectId,
        },
      });

      const projectLibrarires = await getProjectCodeLibraries(projectId);

      const transformedProjectLibrary = projectLibrarires.map(l => ({
        ...l,
        name: l.fieldLabel,
        id: l.fieldAlias,
        isInternal: true,
      }));

      return [
        ...[
          {
            id: 'divider',
            name: 'codeLibraries',
          },
        ],
        ...libraries,
        ...[
          {
            id: 'divider',
            name: 'projectCodeLibraries',
          },
        ],
        ...transformedProjectLibrary,
      ];
    },
  );

  const value = useMemo(() => {
    return (
      libraries?.find(l => l.id === formik.values.optionsSourceCodeLibraryId) ??
      null
    );
  }, [libraries, formik.values.optionsSourceCodeLibraryId]);

  return (
    <Autocomplete
      disabled={disabled}
      loading={isLoading}
      options={libraries ?? []}
      groupBy={option => {
        if (option.id === 'divider') {
          return t(`forms.fieldAttributes.options.${option.name}`);
        }
        return '';
      }}
      getOptionLabel={(option: CodeLibrary) => {
        const localizationKey = `forms.codeLibraries.${option.name}`;
        const localizationExists = i18n.exists(localizationKey);
        return localizationExists ? t(localizationKey) : option.name;
      }}
      onChange={async (_, value) => {
        formik.setFieldValue(
          'defaultValue',
          formik.values.isMultiSelect ? [] : null,
        );

        if (value === null) {
          formik.setFieldValue('optionsSourceCodeLibraryId', null);
          formik.setFieldValue('options', DEFAULT_SELECT_OPTIONS);
          formik.setFieldValue('optionsSourceType', null);
          formik.setFieldValue('optionsSourceFieldTemplateId', '');
          formik.setFieldValue('optionsSourceFieldAlias', '');
          return;
        }

        if (value?.isInternal) {
          formik.setFieldValue(
            'optionsSourceType',
            OptionSourceType.FieldTemplate,
          );
          formik.setFieldValue(
            'optionsSourceFieldTemplateId',
            value.fieldTemplateId,
          );
          formik.setFieldValue('optionsSourceFieldAlias', value.fieldAlias);
        } else {
          formik.setFieldValue(
            'optionsSourceType',
            OptionSourceType.CodeLibrary,
          );
        }
        // this is needed because of an inconsistency with formik and react rendering
        // if this would be sync when formik validates optionsSourceCodeLibraryId the value will always be undefined
        try {
          await formik.setFieldValue('options', []);
          await formik.setFieldValue('optionsSourceCodeLibraryId', value?.id);
        } catch (e) {
          console.error('nincs itt semmi látnivaló, kérem haladjanak tovább');
        }
      }}
      value={value}
      renderOption={(props, option) => {
        const localizationKey = `forms.codeLibraries.${option.name}`;
        const localizationExists = i18n.exists(localizationKey);

        return (
          option.id !== 'divider' && (
            <li
              {...props}
              key={`code-library-autocomplete-option-${option.id}`}
            >
              {localizationExists ? t(localizationKey) : option.name}
            </li>
          )
        );
      }}
      renderInput={params => (
        <TextField
          {...params}
          label={t('forms.fieldAttributes.codeSource')}
          name="optionsSourceCodeLibraryId"
          InputLabelProps={{
            shrink: true,
          }}
          children={null}
          disabled={false}
        />
      )}
    />
  );
}
